import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    ComponentType,
    enumMapper,
    FeatureFlag,
    IAlert,
    IQueueItem,
    NotificationState,
    SeverityType,
} from '@models';
import { AlertDetailsModalComponent } from '@pages/alerts/alert.details.modal';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import { JobDetailsModalComponent } from '@pages/jobs/modals';
import { TransactionDetailsModalComponent } from '@pages/transactions/modals';
import {
    AlertsService,
    JobService,
    QueueItemService,
    SignalRService,
} from '@services';

import { merge } from 'rxjs';
import {
    takeUntil,
    tap,
} from 'rxjs/operators';

@Component({
    templateUrl: './alerts.page.html',
    styleUrls: ['./alerts.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        QueueItemService,
        JobService, {
            provide: GRID_SERVICE,
            useExisting: AlertsService,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class AlertsPageComponent extends ODataGridComponent<IAlert, string> implements OnInit, OnDestroy {
    public severityList = enumMapper(SeverityType, {
        resourceKey: 'SeverityType',
    });

    public componentsMap = enumMapper(ComponentType, {
        resourceKey: 'ComponentType',
        filter: (key) => this._filterAlertComponent(key),
    });

    public defaultSeverity = this.severityList
        .find(s => s.value === SeverityType.Info.toString());

    public stateList = enumMapper(NotificationState, {
        resourceKey: 'NotificationState',
    });
    public defaultState = this.stateList
        .find(s => s.value === NotificationState.Unread.toString());
    public get currentFolderId() { return this._appState.user.folder.value && this._appState.user.folder.value.id; }

    private _queueItemService = this._injector.get<QueueItemService>(QueueItemService);
    private _jobService = this._injector.get<JobService>(JobService);
    private _signalRService = this._injector.get<SignalRService>(SignalRService);
    private _alertsService = this._injector.get<AlertsService>(AlertsService);

    ngOnInit() {
        merge(
            this._signalRService.notification$,
            this._appState.language.change$,
        )
            .pipe(
                tap(() => this._refresh$.next()),
                takeUntil(this._destroyed$),
            )
            .subscribe();
    }

    public showDetails(alert: IAlert) {
        switch (alert.component) {
            case 'Transactions':
                this._queueItemService
                    .getInstance(alert.data.queueItemId!)
                    .pipe(takeUntil(this._destroyed$))
                    .subscribe(
                        (queueItem: IQueueItem) => {
                            this.markAsRead([alert]);
                            this._modal.openModal(TransactionDetailsModalComponent, queueItem, { size: 'lg', autoFocus: false });
                        });
                break;
            case 'Jobs':
                this._jobService
                    .getInstance(alert.data.jobId!, ['Robot', 'Release'])
                    .pipe(takeUntil(this._destroyed$))
                    .subscribe(
                        job => {
                            this.markAsRead([alert]);
                            this._modal.openModal(JobDetailsModalComponent, job);
                        });
                break;
            case 'Process':
                this.markAsRead([alert]);
                this._modal.openModal(AlertDetailsModalComponent, alert.data);
                break;
        }
    }

    public markAsRead(alerts: IAlert[] = this._grid.selectionManager.selected) {
        const alertsPayload = alerts
            .filter(a => a.state === 'Unread')
            .map(a => a.userNotificationId);

        if (!alertsPayload.length) { return; }

        this._alertsService.markAsRead(alertsPayload)
            .pipe(tap(() => this._refresh$.next()))
            .subscribe();
    }

    public classConfig =
        (entry: IAlert) => ({
            'alert-unread': entry.state === 'Unread',
            'alert-read': entry.state === 'Read',
        })

    public get allowMarkAsRead() {
        return this._grid.selectionManager.hasValue() &&
            this._grid.selectionManager.selected
                .some((a: IAlert) => a.state === 'Unread');
    }

    private _filterAlertComponent(key: string) {
        if (!isNaN(Number.parseInt(key, 10))) {
            return false;
        }
        switch (key) {
            case 'Tasks':
                return this._appState.settings.isFeatureEnabled(FeatureFlag.Tasks);
            default:
                return true;
        }
    }
}
